/* =========================================================
   珂玺资本 — 后台「实时预览」内联编辑模式
   仅当 URL 带 ?edit=1 时激活；普通访客零影响。

   工作方式（不改任何页面组件）：
   1. 反查索引：遍历 window.SiteData / window.I18N 所有字符串叶子，
      建立「文字 → 内容路径」映射（path 如 site_data.sectors.0.name.cn
      或 i18n.cn.main.t001）。
   2. DOM 扫描：渲染后遍历文本节点，按 trim 文字唯一命中索引，
      给最近的元素打 data-cpath 标记。MutationObserver 持续跟进。
   3. 交互：hover 高亮带标记的元素；click 把 path 经 postMessage
      上报给父窗口（admin）。
   4. 接收：父窗口保存后 postMessage 回来，深合并进 SiteData/I18N
      并调用 window.__kexiForceRerender() 让官网即时刷新。
   ========================================================= */
(function () {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (params.get("edit") !== "1") return;

  const ATTR = "data-cpath";
  let index = null;            // Map<string, string[]>  文字 -> 候选 path 列表
  let currentLang = localStorage.getItem("kexi_lang") || "cn";

  // ---------- 反查索引 ----------
  function walk(node, prefix, out) {
    if (node == null) return;
    if (typeof node === "string") {
      const s = node.trim();
      if (s.length === 0) return;
      if (!out.has(s)) out.set(s, []);
      out.get(s).push(prefix);
      return;
    }
    if (typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${prefix}.${i}`, out));
    } else {
      for (const k of Object.keys(node)) walk(node[k], `${prefix}.${k}`, out);
    }
  }

  function buildIndex() {
    const out = new Map();
    if (window.SiteData) walk(window.SiteData, "site_data", out);
    if (window.I18N) walk(window.I18N, "i18n", out);
    index = out;
  }

  // path 对应当前语言？i18n.<lang>.* 仅在该 lang 匹配；site_data 的 cn/en 叶子按后缀。
  function pathMatchesLang(path) {
    if (path.startsWith("i18n.")) {
      const seg = path.split(".")[1];
      return seg === currentLang;
    }
    // site_data：若 path 末段是 cn/en，只取当前语言那条
    const last = path.split(".").pop();
    if (last === "cn") return currentLang === "cn";
    if (last === "en") return currentLang === "en";
    return true; // 非双语叶子（数字、ticker 等）两种语言都显示
  }

  // ---------- DOM 标记 ----------
  function markTextNode(textNode) {
    const raw = textNode.nodeValue;
    if (!raw) return;
    const s = raw.trim();
    if (s.length < 1) return;
    const cands = index.get(s);
    if (!cands || cands.length === 0) return;
    // 只保留与当前语言匹配的候选
    const langCands = cands.filter(pathMatchesLang);
    if (langCands.length === 0) return;

    const el = textNode.parentElement;
    if (!el || el.closest("[contenteditable]")) return;
    // data-cms-skip / data-cms-redirect 区域不进入文案编辑
    // （装饰序号；或有专门菜单维护的列表 —— 后者右键时给跳转提示）
    if (el.closest("[data-cms-skip]") || el.closest("[data-cms-redirect]")) return;
    // 显式绑定字段的元素由 markFixed 处理，反查时跳过（避免同文字多处歧义）
    if (el.closest("[data-cpath-fixed]")) return;
    el.setAttribute(ATTR, langCands[0]);
    if (langCands.length > 1) el.setAttribute("data-cpath-ambiguous", "1");
  }

  // 显式绑定：元素上 data-cpath-fixed="<lang 中性 path，如 aux.t056>" 直接定到该字段，
  // 不走文字反查，解决同一文字（如序号 01.）在多页复用导致的歧义。
  function markFixed(el) {
    if (el.closest("[data-cms-skip]") || el.closest("[data-cms-redirect]")) return;
    const base = el.getAttribute("data-cpath-fixed");
    if (!base) return;
    // base 形如 "aux.t056" → 当前语言下补成 i18n.<lang>.aux.t056
    const path = base.startsWith("i18n.") || base.startsWith("site_data.")
      ? base : `i18n.${currentLang}.${base}`;
    el.setAttribute(ATTR, path);
  }

  function scan(root) {
    if (!index) buildIndex();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(n) {
        if (!n.nodeValue || n.nodeValue.trim().length === 0) return NodeFilter.FILTER_REJECT;
        const p = n.parentElement;
        if (!p) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName;
        if (tag === "SCRIPT" || tag === "STYLE") return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    // 先处理显式绑定字段的元素（优先于反查，避免被同文字反查覆盖）
    root.querySelectorAll?.("[data-cpath-fixed]").forEach(markFixed);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(markTextNode);
    // placeholder 不是文本节点，单独反查标记（编辑后重渲染会自动刷新提示语）
    root.querySelectorAll?.("input[placeholder], textarea[placeholder]").forEach(markPlaceholder);
    decorateLists();
  }

  // ---------- 可增删列表（发展历程 / 团队）----------
  // 给 [data-cms-list] 容器的每个子项注入「移动 / 删除」控件，容器末尾注入「新增」按钮。
  // 控件操作经 postMessage 上报后台，由后台改数组并回写（数组整体替换重渲染）。
  function makeBtn(label, title, onClick) {
    const b = document.createElement("button");
    b.className = "cms-listbtn";
    b.type = "button";
    b.textContent = label;
    b.title = title;
    b.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); onClick(); });
    b.addEventListener("contextmenu", (ev) => { ev.preventDefault(); ev.stopPropagation(); });
    return b;
  }
  function sendListOp(op, list, index) {
    parent.postMessage({ source: "kexi-edit", type: "list-op", op, list, index }, "*");
  }
  function decorateLists() {
    document.querySelectorAll("[data-cms-list]").forEach((container) => {
      const list = container.getAttribute("data-cms-list");
      const horizontal = container.getAttribute("data-cms-list-dir") === "horizontal";
      const items = container.querySelectorAll(":scope > [data-cms-list-item]");
      items.forEach((item, i) => {
        if (item.querySelector(":scope > .cms-listtools")) return; // 已注入
        if (getComputedStyle(item).position === "static") item.style.position = "relative";
        const tools = document.createElement("div");
        tools.className = "cms-listtools";
        const prev = makeBtn(horizontal ? "‹" : "↑", horizontal ? "左移" : "上移", () => sendListOp("move-up", list, i));
        const next = makeBtn(horizontal ? "›" : "↓", horizontal ? "右移" : "下移", () => sendListOp("move-down", list, i));
        if (i === 0) prev.disabled = true;
        if (i === items.length - 1) next.disabled = true;
        const del = makeBtn("×", "删除", () => sendListOp("delete", list, i));
        del.classList.add("cms-listbtn-danger");
        tools.appendChild(prev); tools.appendChild(next); tools.appendChild(del);
        item.appendChild(tools);
      });
      // 末尾「新增」按钮（容器外，紧随其后）
      if (container.nextSibling && container.nextSibling.classList?.contains("cms-listadd")) return;
      const add = document.createElement("button");
      add.className = "cms-listadd";
      add.type = "button";
      add.textContent = "＋ 新增一项";
      add.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); sendListOp("add", list, items.length); });
      add.addEventListener("contextmenu", (ev) => { ev.preventDefault(); ev.stopPropagation(); });
      container.parentNode.insertBefore(add, container.nextSibling);
    });
  }
  function clearListDecor() {
    document.querySelectorAll(".cms-listtools").forEach((n) => n.remove());
    document.querySelectorAll(".cms-listadd").forEach((n) => n.remove());
  }

  // 给带 placeholder 的输入框打标记（path 指向该 placeholder 文字对应的字段）
  function markPlaceholder(el) {
    if (el.hasAttribute(ATTR)) return;
    if (el.closest("[data-cms-skip]") || el.closest("[data-cms-redirect]")) return;
    const s = (el.getAttribute("placeholder") || "").trim();
    if (!s) return;
    const cands = index.get(s);
    if (!cands) return;
    const langCands = cands.filter(pathMatchesLang);
    if (langCands.length === 0) return;
    el.setAttribute(ATTR, langCands[0]);
    el.setAttribute("data-cpath-ph", "1");          // 标记这是 placeholder 型，hover 提示用
    if (langCands.length > 1) el.setAttribute("data-cpath-ambiguous", "1");
  }

  function rescanAll() {
    // 清掉旧标记后整树重扫（语言/内容变化时）
    document.querySelectorAll(`[${ATTR}]`).forEach((el) => {
      el.removeAttribute(ATTR);
      el.removeAttribute("data-cpath-ambiguous");
      el.removeAttribute("data-cpath-ph");
    });
    clearListDecor();
    buildIndex();
    scan(document.body);
  }

  // ---------- 交互：hover 高亮 + click 上报 ----------
  let hovered = null;
  function onOver(e) {
    // redirect 区域整块高亮（提示右键可跳转到专门菜单）；图片元素整块高亮；否则高亮单个可编辑文字
    const el = e.target.closest("[data-cms-redirect]") || e.target.closest("[data-cms-image]") || e.target.closest(`[${ATTR}]`);
    if (el === hovered) return;
    if (hovered) hovered.classList.remove("cms-hl", "cms-hl-redirect");
    hovered = el;
    if (hovered) {
      hovered.classList.add("cms-hl");
      if (hovered.hasAttribute("data-cms-redirect")) hovered.classList.add("cms-hl-redirect");
    }
  }
  // 左键：保持网站原本行为（按钮触发功能、链接跳转），不拦截。
  // 右键：进入编辑——上报选中文字给后台。编辑态下浏览器右键菜单无用，拦掉。
  function onContextMenu(e) {
    // 优先：落在「有专门菜单维护」的区域 → 上报跳转提示，不进入文案编辑
    const redirect = e.target.closest("[data-cms-redirect]");
    if (redirect) {
      e.preventDefault();
      e.stopPropagation();
      document.querySelectorAll(".cms-sel").forEach((n) => n.classList.remove("cms-sel"));
      redirect.classList.add("cms-sel");
      parent.postMessage({
        source: "kexi-edit",
        type: "pick-redirect",
        target: redirect.getAttribute("data-cms-redirect"),
        targetId: redirect.getAttribute("data-cms-redirect-id") || null,
        lang: currentLang,
      }, "*");
      return;
    }

    // 图片元素：上报图片字段（后台显示上传 + 宽高适配）
    const imageEl = e.target.closest("[data-cms-image]");
    if (imageEl) {
      e.preventDefault();
      e.stopPropagation();
      document.querySelectorAll(".cms-sel").forEach((n) => n.classList.remove("cms-sel"));
      imageEl.classList.add("cms-sel");
      parent.postMessage({
        source: "kexi-edit",
        type: "pick",
        kind: "image",
        path: imageEl.getAttribute("data-cms-image"),
        lang: currentLang,
      }, "*");
      return;
    }

    const el = e.target.closest(`[${ATTR}]`);
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    document.querySelectorAll(".cms-sel").forEach((n) => n.classList.remove("cms-sel"));
    el.classList.add("cms-sel");
    // 若文字所在元素（或祖先）是带链接的按钮，附带链接字段，后台面板加 URL 输入框
    const linkEl = el.closest("[data-cms-link]");
    parent.postMessage({
      source: "kexi-edit",
      type: "pick",
      path: el.getAttribute(ATTR),
      ambiguous: el.hasAttribute("data-cpath-ambiguous"),
      // 语言中性字段（如装饰序号）：后台只显示一个输入框，编辑同步写中英
      neutral: el.closest("[data-cms-neutral]") != null,
      linkPath: linkEl ? linkEl.getAttribute("data-cms-link") : null,  // 关联链接字段（可选）
      text: el.textContent.trim(),
      lang: currentLang,
    }, "*");
  }

  // ---------- 接收父窗口消息 ----------
  function deepMergeInPlace(target, source) {
    for (const k of Object.keys(source)) {
      const sv = source[k], tv = target[k];
      if (sv && typeof sv === "object" && !Array.isArray(sv) &&
          tv && typeof tv === "object" && !Array.isArray(tv)) {
        deepMergeInPlace(tv, sv);
      } else target[k] = sv;
    }
  }

  window.addEventListener("message", (e) => {
    const m = e.data;
    if (!m || m.source !== "kexi-admin") return;
    if (m.type === "update") {
      if (m.site_data && window.SiteData) deepMergeInPlace(window.SiteData, m.site_data);
      if (m.i18n && window.I18N) deepMergeInPlace(window.I18N, m.i18n);
      window.__kexiForceRerender && window.__kexiForceRerender();
      setTimeout(rescanAll, 60);
    } else if (m.type === "navigate" && m.route != null) {
      // B 档真实路由：pushState 改 pathname（保留 ?edit=1），再触发 popstate 让 App 同步
      const path = `/${m.route === "home" ? "" : m.route}`;
      window.history.pushState({ route: m.route }, "", path + window.location.search);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else if (m.type === "setLang" && m.lang) {
      localStorage.setItem("kexi_lang", m.lang);
      currentLang = m.lang;
      window.dispatchEvent(new Event("storage"));
      setTimeout(rescanAll, 60);
    }
  });

  // ---------- 启动 ----------
  function start() {
    document.body.classList.add("cms-edit");
    const style = document.createElement("style");
    style.textContent = `
      .cms-edit [${ATTR}] { cursor: context-menu; transition: outline-color .12s, background .12s; outline: 1px dashed transparent; outline-offset: 2px; }
      .cms-edit .cms-hl { outline-color: var(--gold, #c9a86b); background: rgba(201,168,107,0.10); }
      .cms-edit .cms-sel { outline: 2px solid var(--gold, #c9a86b) !important; background: rgba(201,168,107,0.18) !important; }
      /* 有专门菜单维护的区域：蓝色虚框，提示右键跳转而非就地编辑 */
      .cms-edit .cms-hl-redirect { outline: 1px dashed #5b8def !important; background: rgba(91,141,239,0.07) !important; cursor: context-menu; }
      .cms-tip {
        position: fixed; left: 50%; bottom: 16px; transform: translateX(-50%);
        z-index: 99999; pointer-events: none;
        background: rgba(20,24,40,0.92); color: #f5f1e8;
        font-size: 12px; line-height: 1.5; letter-spacing: .02em;
        padding: 7px 16px; border-radius: 20px;
        font-family: -apple-system, "PingFang SC", sans-serif;
        box-shadow: 0 4px 18px rgba(0,0,0,0.3);
      }
      .cms-tip b { color: var(--gold, #c9a86b); font-weight: 600; }
      /* 列表增删控件 */
      .cms-listtools {
        position: absolute; top: 8px; right: 8px; z-index: 50;
        display: flex; gap: 8px;
        opacity: 0; transition: opacity .2s; pointer-events: none;
      }
      [data-cms-list-item]:hover > .cms-listtools { opacity: 1; pointer-events: auto; }
      /* 参考 top 按钮：方形、深底金边、圆角，hover 反色 */
      .cms-listbtn {
        width: 28px; height: 28px; line-height: 1;
        display: flex; align-items: center; justify-content: center;
        background: var(--bg-1, #0e1430); color: var(--gold, #c9a86b);
        border: 1px solid var(--gold, #c9a86b); border-radius: 4px;
        font-size: 13px; cursor: pointer; padding: 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        font-family: -apple-system, "PingFang SC", sans-serif;
        transition: background .2s, color .2s;
      }
      .cms-listbtn:hover:not(:disabled) { background: var(--gold, #c9a86b); color: var(--bg-1, #0e1430); }
      .cms-listbtn:disabled { opacity: 0.35; cursor: not-allowed; }
      .cms-listbtn-danger { border-color: #d66; color: #e88; }
      .cms-listbtn-danger:hover { background: #d66; color: #fff; }
      .cms-listadd {
        display: block; margin: 28px auto 0;
        padding: 12px 32px;
        background: var(--bg-1, #0e1430); color: var(--gold, #c9a86b);
        border: 1px solid var(--gold, #c9a86b); border-radius: 4px;
        font-size: 13px; cursor: pointer; letter-spacing: .04em;
        box-shadow: 0 8px 24px rgba(0,0,0,0.25);
        font-family: -apple-system, "PingFang SC", sans-serif;
        transition: background .2s, color .2s;
      }
      .cms-listadd:hover { background: var(--gold, #c9a86b); color: var(--bg-1, #0e1430); }
    `;
    document.head.appendChild(style);

    // 底部提示条：说明右键编辑、左键执行原功能
    const tip = document.createElement("div");
    tip.className = "cms-tip";
    tip.innerHTML = '编辑模式 · <b>右键</b>点文字进入编辑 · <b>左键</b>执行按钮/链接原功能';
    document.body.appendChild(tip);

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("contextmenu", onContextMenu, true);

    let raf = 0;
    const obs = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => scan(document.body));
    });
    obs.observe(document.body, { childList: true, subtree: true });

    // 语言切换（官网 useLang 写 localStorage）→ 重扫
    window.addEventListener("storage", () => {
      const l = localStorage.getItem("kexi_lang") || "cn";
      if (l !== currentLang) { currentLang = l; setTimeout(rescanAll, 60); }
    });

    // 路由变化上报父窗口，让后台记住 iframe 当前停在哪页（用于跳转后返回定位）。
    // 官网 setRoute 用 history.pushState 改 hash，不触发 hashchange，故用轮询兜底。
    let lastRoute = null;
    const reportRoute = () => {
      const r = window.location.pathname.replace(/^\//, "") || "home";
      if (r === lastRoute) return;
      lastRoute = r;
      parent.postMessage({ source: "kexi-edit", type: "route", route: r }, "*");
    };
    window.addEventListener("popstate", reportRoute);
    setInterval(reportRoute, 400);

    setTimeout(() => { scan(document.body); parent.postMessage({ source: "kexi-edit", type: "ready" }, "*"); reportRoute(); }, 300);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
